import type { StoreApi, UseBoundStore } from "zustand";
import { shallow } from "zustand/shallow";

type GenericState = Record<string, any>;

/**
 * Creates a custom store hook with selectors for a Zustand store.
 *
 * @template T - The type of the store state.
 * @param {UseBoundStore<StoreApi<T>>} store - The Zustand store.
 * @returns {<K extends keyof T>(keys: K[]) => Pick<T, K>} - The custom store hook that accepts an array of keys and returns a subset of the store state.
 */
const createStoreWithSelectors = <T extends GenericState>(
	store: UseBoundStore<StoreApi<T>>,
): (<K extends keyof T>(keys: K[]) => Pick<T, K>) => {
	const useStore: <K extends keyof T>(keys: K[]) => Pick<T, K> = <
		K extends keyof T,
	>(
		keys: K[],
	) => {
		return store((state) => {
			const x = keys.reduce((acc, cur) => {
				acc[cur] = state[cur];
				return acc;
			}, {} as T);

			return x as Pick<T, K>;
		}, shallow);
	};

	return useStore;
};

export { createStoreWithSelectors };
